import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import {
  countryCodes,
  isSameCountryCode,
} from "@/react/utils/paywalls/countryCodes";
import {
  getBrazilCpfTaxIdType,
  getTaxIdTypeByLabel,
  mapTaxIdTypeToOptions,
} from "@/react/utils/paywalls/taxId/helpers";
import { getTaxIdTypes } from "@/react/utils/paywalls/taxId/taxIdTypes";

export interface UseTaxIdFormConfig {
  fieldNames: {
    addressCountry: string;
    taxIdType: string;
    taxIdTypeLabel: string;
    taxIdValue: string;
  };
  isBrazilianCommunity: boolean;
  isTaxCollectionEnabled: boolean;
  isTaxIdMandatory?: boolean;
  selectedCountry?: string;
}

export const useTaxIdForm = ({
  fieldNames,
  isBrazilianCommunity,
  isTaxCollectionEnabled,
  isTaxIdMandatory,
  selectedCountry,
}: UseTaxIdFormConfig) => {
  const {
    taxIdType: taxIdTypeFieldName,
    taxIdTypeLabel: taxIdTypeLabelFieldName,
    taxIdValue: taxIdValueFieldName,
  } = fieldNames;

  const { setValue, watch, clearErrors } =
    useFormContext<Record<string, string>>();

  // Watch the appropriate tax ID type field (label for profile, type for checkout)
  const taxIdTypeLabel = watch(taxIdTypeLabelFieldName);
  const selectedTaxIdType = getTaxIdTypeByLabel(taxIdTypeLabel);
  const previousCountryRef = useRef<string | null>(null);

  // Tax ID is required when the processor mandates it OR when Brazil is selected
  const isRequired =
    Boolean(isTaxIdMandatory) ||
    isSameCountryCode(selectedCountry, countryCodes.br);

  // When the processor mandates a tax ID (BR Stripe), treat the effective country
  // as Brazil regardless of the member's billing address country. This ensures the
  // dropdown shows CPF/CNPJ and country-matching logic uses BR instead of the
  // member's stored country (e.g. US).
  const effectiveTaxIdCountry = isTaxIdMandatory
    ? countryCodes.br
    : selectedCountry;

  // Filter tax ID types based on effective tax ID country
  const applicableTaxIdTypes = useMemo(() => {
    if (!effectiveTaxIdCountry) return getTaxIdTypes();

    return getTaxIdTypes().filter(type =>
      isSameCountryCode(type.countryCode, effectiveTaxIdCountry),
    );
  }, [effectiveTaxIdCountry]);

  // Get tax ID type options for select dropdown
  const taxIdTypeOptions = useMemo(
    () => applicableTaxIdTypes.map(mapTaxIdTypeToOptions),
    [applicableTaxIdTypes],
  );

  // Tax ID fields are visible for:
  // - All Brazilian communities
  // - Non-Brazilian communities with tax collection enabled
  // - Communities where the processor mandates a tax ID
  //
  // that have tax ID types available
  const isVisible = useMemo(
    () =>
      (isBrazilianCommunity ||
        isTaxCollectionEnabled ||
        Boolean(isTaxIdMandatory)) &&
      applicableTaxIdTypes.length > 0,
    [
      isBrazilianCommunity,
      isTaxCollectionEnabled,
      isTaxIdMandatory,
      applicableTaxIdTypes,
    ],
  );

  // Options with conditional clear (only when not required)
  const selectOptions = useMemo(() => {
    const options = [...taxIdTypeOptions];

    // Add clear option when allowed AND not required
    if (!isRequired) {
      options.unshift({
        value: "",
        text: t("not_applicable"),
      });
    }

    return options;
  }, [taxIdTypeOptions, isRequired]);

  // Effect 1: Set form values when tax ID type changes
  useEffect(() => {
    if (selectedTaxIdType) {
      setValue(taxIdTypeFieldName, selectedTaxIdType.enum);
    } else {
      setValue(taxIdTypeFieldName, "");
      setValue(taxIdValueFieldName, "");
    }
  }, [selectedTaxIdType, setValue, taxIdTypeFieldName, taxIdValueFieldName]);

  // Effect 2: Sync tax ID selection on country change
  useEffect(() => {
    const currentCountry = effectiveTaxIdCountry || null;
    const isCountryChange = previousCountryRef.current !== currentCountry;

    if (
      currentCountry &&
      selectedTaxIdType &&
      !isSameCountryCode(selectedTaxIdType.countryCode, currentCountry)
    ) {
      setValue(taxIdTypeLabelFieldName, "");
    }

    if (
      isCountryChange &&
      currentCountry &&
      applicableTaxIdTypes.length === 1
    ) {
      setValue(taxIdTypeLabelFieldName, applicableTaxIdTypes[0].label, {
        shouldDirty: true,
      });
    }

    previousCountryRef.current = currentCountry;
  }, [
    effectiveTaxIdCountry,
    selectedTaxIdType,
    applicableTaxIdTypes,
    setValue,
    taxIdTypeLabelFieldName,
  ]);

  // Effect 3: Pre-fill Brazilian CPF when mandatory for Brazilian communities
  useEffect(() => {
    // Only pre-fill if:
    // 1. It's a Brazilian community AND
    // 2. Tax ID is required AND
    // 3. No tax ID type is currently selected
    if (isBrazilianCommunity && isRequired && !taxIdTypeLabel) {
      const brazilCpfTaxIdType = getBrazilCpfTaxIdType();
      if (brazilCpfTaxIdType) {
        setValue(taxIdTypeLabelFieldName, brazilCpfTaxIdType.label, {
          shouldDirty: true,
        });
      }
    }
  }, [
    isBrazilianCommunity,
    setValue,
    isRequired,
    taxIdTypeLabelFieldName,
    taxIdTypeLabel,
  ]);

  const handleTaxIdTypeChange = useCallback(
    ({ value }: { value: string }) => {
      setValue(taxIdTypeLabelFieldName, value, { shouldDirty: true });
      clearErrors(taxIdValueFieldName);
    },
    [setValue, taxIdTypeLabelFieldName, clearErrors, taxIdValueFieldName],
  );

  // Tax ID value is required if:
  // 1. Country requires it (Brazil + Brazilian community/tax collection), OR
  // 2. Processor mandates it (BR Stripe + BRL currency), OR
  // 3. User has selected a tax ID type
  const isTaxIdValueRequired = isRequired || Boolean(selectedTaxIdType);

  return {
    handleTaxIdTypeChange,
    isRequired,
    isTaxIdValueRequired,
    isVisible,
    selectedTaxIdType,
    selectOptions,
    taxIdTypeLabel,
  };
};
